import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Grid,
  Divider,
  IconButton,
  Popover,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoIcon from "@mui/icons-material/Info";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";

// API imports
import { usersVisibleLeavePolicy, createLeaveRequest } from "api/users";
import { calendarSx } from "assets/theme/commonCalendarSx";
import { useTheme } from "@mui/material/styles";
import { dateInputTextSx } from "assets/theme/commonCalendarSx";
import { dateInputBoxSx } from "assets/theme/commonCalendarSx";

function ApplyLeaveForm({ selectedLeaveType, handleClose }) {
  const theme = useTheme();

  const userId = localStorage.getItem("loggedUserId");

  // State Management
  const [leaveType, setLeaveType] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedStartDate, setSelectedStartDate] = useState(dayjs());
  const [selectedEndDate, setSelectedEndDate] = useState(dayjs());
  const [anchorStart, setAnchorStart] = useState(null);
  const [anchorEnd, setAnchorEnd] = useState(null);

  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveOptions, setLeaveOptions] = useState([]);
  const [dateLeaves, setDateLeaves] = useState([]);

  const [balanceData, setBalanceData] = useState({
    availableBalance: 0,
    currentBooking: 0,
    balanceAfterBooking: 0,
    estimatedBalance: 0,
  });

  // Selected leave policy details
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  // Fetch leave balances
  useEffect(() => {
    fetchLeaveBalances();
  }, [userId]);

  // Handle external selected leave type
  useEffect(() => {
    if (selectedLeaveType) setLeaveType(selectedLeaveType);
  }, [selectedLeaveType]);

  // Update selected policy when leaveType changes
  useEffect(() => {
    if (leaveType && leaveBalances.length > 0) {
      const policy = leaveBalances.find((policy) => policy.id === parseInt(leaveType));
      setSelectedPolicy(policy);
    } else {
      setSelectedPolicy(null);
    }
  }, [leaveType, leaveBalances]);

  // Generate date list when date range changes
  useEffect(() => {
    setDateLeaves(generateDateRange());
  }, [selectedStartDate, selectedEndDate]);

  // Recalculate balance summary
  useEffect(() => {
    if (selectedPolicy) {
      const available = selectedPolicy.stats.available;
      const daysRequested = calculateTotalDays();
      const balanceAfter = available - daysRequested;

      // Calculate remaining leaves for expected balance
      const estimatedRemaining = Math.max(0, available - daysRequested);

      setBalanceData({
        availableBalance: available,
        currentBooking: daysRequested,
        balanceAfterBooking: Math.max(balanceAfter, 0),
        estimatedBalance: estimatedRemaining, // Changed to show remaining leaves
      });
    }
  }, [selectedPolicy, dateLeaves]);

  // Get max selectable days based on available balance
  const getMaxSelectableDays = () => {
    if (!selectedPolicy) return 0;
    return selectedPolicy.stats.available;
  };

  // Get max selectable date based on available balance
  const getMaxSelectableDate = () => {
    if (!selectedStartDate || !selectedPolicy) return null;
    const maxDays = getMaxSelectableDays();
    return selectedStartDate.add(maxDays - 1, "day");
  };

  // --- Helper Functions ---
  const fetchLeaveBalances = async () => {
    try {
      setLoading(true);
      const response = await usersVisibleLeavePolicy(userId);
      if (response) {
        const leavePolicies = response;
        setLeaveBalances(leavePolicies);
        const options = leavePolicies.map((policy) => ({
          value: policy.id,
          label: `${policy.leave_name} (${policy.leave_code}) - ${policy.stats.available} available`,
        }));
        setLeaveOptions(options);
      }
    } catch (err) {
      console.error("Error fetching leave balances:", err);
      setError("Failed to load leave balances");
    } finally {
      setLoading(false);
    }
  };

  const getDayValue = (type) => {
    if (type === "first_half" || type === "second_half") {
      return 0.5;
    }
    return 1; // full day
  };

  const calculateTotalDays = () =>
    dateLeaves.reduce((total, d) => total + getDayValue(d.leaveType), 0);

  const generateDateRange = () => {
    if (!selectedStartDate || !selectedEndDate) return [];

    const start = dayjs(selectedStartDate).startOf("day");
    const end = dayjs(selectedEndDate).startOf("day");

    // Limit end date based on available balance
    const maxDays = getMaxSelectableDays();
    const calculatedEnd = start.add(maxDays - 1, "day");
    const finalEnd = end.isAfter(calculatedEnd) ? calculatedEnd : end;

    const totalDays = finalEnd.diff(start, "day") + 1;

    return Array.from({ length: totalDays }, (_, i) => {
      const current = start.add(i, "day");
      return {
        date: current,
        dayName: current.format("ddd"),
        formattedDate: current.format("YYYY-MM-DD"),
        leaveType: "full",
      };
    });
  };

  const handleDateLeaveTypeChange = (index, type) => {
    const updated = [...dateLeaves];
    updated[index].leaveType = type;
    setDateLeaves(updated);
  };

  const handleStartDateChange = (val) => {
    setSelectedStartDate(val);
    setAnchorStart(null);

    // If end date is before start date or exceeds max limit, adjust it
    if (selectedEndDate.isBefore(val)) {
      setSelectedEndDate(val);
    } else if (selectedPolicy) {
      const maxDays = getMaxSelectableDays();
      const maxDate = val.add(maxDays - 1, "day");
      if (selectedEndDate.isAfter(maxDate)) {
        setSelectedEndDate(maxDate);
      }
    }
  };

  const handleEndDateChange = (val) => {
    if (!selectedPolicy) return;

    const maxDays = getMaxSelectableDays();
    const maxDate = selectedStartDate.add(maxDays - 1, "day");

    if (val.isAfter(maxDate)) {
      setError(`You can only select maximum ${maxDays} days for ${selectedPolicy.leave_name}`);
      setSelectedEndDate(maxDate);
    } else {
      setError("");
      setSelectedEndDate(val);
    }
    setAnchorEnd(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!leaveType) return setError("Please select a leave type.");

    // Check if selected days exceed available balance
    const totalDays = calculateTotalDays();
    const availableDays = selectedPolicy?.stats?.available || 0;

    if (totalDays > availableDays) {
      setError("Not enough balance");
      return;
    }

    if (totalDays <= 0) {
      return setError("Please select at least one day for leave");
    }

    // breakdown mapping for backend
    const breakdown = {};
    dateLeaves.forEach((d) => {
      breakdown[d.formattedDate] = d.leaveType;
    });

    try {
      setLoading(true);

      const leaveRequestData = {
        leave_policy_id: parseInt(leaveType),
        from_date: selectedStartDate.format("YYYY-MM-DD"),
        to_date: selectedEndDate.format("YYYY-MM-DD"),
        reason: reason || "",
        breakdown,
      };

      const res = await createLeaveRequest(leaveRequestData);
      console.log(res);
      setSuccess("Leave request submitted successfully!");
      setTimeout(() => handleClose && handleClose(), 2000);
    } catch (err) {
      console.error("Error submitting leave request:", err);
      setError("Failed to submit leave request");
    } finally {
      setLoading(false);
    }
  };

  const leaveTypeOptions = [
    { value: "full", label: "Full Day" },
    { value: "first_half", label: "First Half" },
    { value: "second_half", label: "Second Half" },
  ];

  // Calculate remaining days that can be selected
  const remainingSelectableDays = useMemo(() => {
    if (!selectedPolicy) return 0;
    const totalSelected = calculateTotalDays();
    const available = selectedPolicy.stats.available;
    return Math.max(0, available - totalSelected);
  }, [selectedPolicy, dateLeaves]);

  // --- JSX ---
  return (
    <SoftBox display="flex" gap={3} justifyContent="center" flexWrap="wrap" sx={{ width: "100vw" }}>
      {/* --- MAIN FORM --- */}
      <Card sx={{ flex: 1, minWidth: 400 }}>
        <SoftBox p={2}>
          {/* Header */}
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <SoftBox display="flex" alignItems="center" gap={1}>
              <DescriptionIcon color="primary" />
              <SoftTypography variant="h5" fontWeight="medium">
                Apply Leave
              </SoftTypography>
            </SoftBox>
            <IconButton onClick={handleClose} size="small" sx={{ color: "text.secondary" }}>
              <CloseIcon />
            </IconButton>
          </SoftBox>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 1, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          {/* Leave Type */}
          <SoftBox mb={1}>
            <SoftTypography variant="h6" fontWeight="medium" >
              Leave Type
            </SoftTypography>
            {loading ? (
              <SoftBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <SoftTypography variant="body2" >
                  Loading leave types...
                </SoftTypography>
              </SoftBox>
            ) : (
              <SoftSelect
                placeholder="Select Leave Type"
                value={leaveOptions.find((opt) => opt.value === parseInt(leaveType)) || null}
                options={leaveOptions}
                onChange={(opt) => {
                  setLeaveType(opt.value);
                  // Reset dates when leave type changes
                  setSelectedStartDate(dayjs());
                  setSelectedEndDate(dayjs());
                }}
                isDisabled={loading}
              />
            )}
          </SoftBox>

          {/* Date Range */}
          <SoftBox mb={1}>
            <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <SoftTypography variant="h6" fontWeight="medium" >
                Select Date Range
              </SoftTypography>
              {selectedPolicy && (
                <Tooltip title={`Maximum ${selectedPolicy.stats.available} days can be selected`}>
                  <InfoIcon fontSize="small" color="info" />
                </Tooltip>
              )}
            </SoftBox>

            {selectedPolicy && (
              <SoftTypography variant="caption" color="info" display="block" mb={2}>
                Available: {selectedPolicy.stats.available} days | Remaining selectable:{" "}
                {remainingSelectableDays} days
              </SoftTypography>
            )}

       <SoftBox display="flex" alignItems="center" gap={2} width="100%">
  {/* Start Date */}
  <SoftBox
    sx={{ ...dateInputBoxSx, flex: 1 }}
    onClick={(e) => setAnchorStart(e.currentTarget)}
  >
    <CalendarTodayIcon fontSize="small" />
    <SoftTypography sx={dateInputTextSx}>
      {selectedStartDate
        ? selectedStartDate.format("DD-MMM-YYYY")
        : "From Date"}
    </SoftTypography>
  </SoftBox>

  <SoftTypography variant="body2" color="text.secondary" fontWeight="medium">
    to
  </SoftTypography>

  {/* End Date */}
  <SoftBox
    sx={{ ...dateInputBoxSx, flex: 1 }}
    onClick={(e) => setAnchorEnd(e.currentTarget)}
  >
    <CalendarTodayIcon fontSize="small" />
    <SoftTypography sx={dateInputTextSx}>
      {selectedEndDate
        ? selectedEndDate.format("DD-MMM-YYYY")
        : "To Date"}
    </SoftTypography>
  </SoftBox>
</SoftBox>


            {/* Start Date Popover */}
            <Popover
              open={Boolean(anchorStart)}
              anchorEl={anchorStart}
              onClose={() => setAnchorStart(null)}
              PaperProps={{
                sx: {
                  backgroundColor: "#ffffff",
                  borderRadius: "10px",
                  boxShadow: "0px 12px 30px rgba(0,0,0,0.25)",
                },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={selectedStartDate}
                  minDate={dayjs()}
                  sx={calendarSx(theme)}
                  onChange={handleStartDateChange}
                />
              </LocalizationProvider>
            </Popover>

            {/* End Date Popover */}
            <Popover
              open={Boolean(anchorEnd)}
              anchorEl={anchorEnd}
              onClose={() => setAnchorEnd(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  boxShadow: 3,
                  padding: 1,
                  zIndex: 9999,
                },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={selectedEndDate}
                  minDate={selectedStartDate || dayjs()}
                  maxDate={getMaxSelectableDate()}
                  sx={calendarSx(theme)}
                  onChange={handleEndDateChange}
                />
              </LocalizationProvider>
            </Popover>
          </SoftBox>

          <Divider sx={{ my: 1 }} />

          {/* Date List */}
          <SoftBox mb={1}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SoftTypography variant="h6" fontWeight="medium" >
                Selected Dates
              </SoftTypography>
              {selectedPolicy && (
                <SoftTypography
                  variant="caption"
                  color={remainingSelectableDays === 0 ? "error" : "info"}
                >
                  {remainingSelectableDays === 0
                    ? "No more days available"
                    : `${remainingSelectableDays} days remaining`}
                </SoftTypography>
              )}
            </SoftBox>

            <SoftBox sx={{ maxHeight: 300, overflowY: "auto" }}>
              {dateLeaves.map((d, i) => (
                <SoftBox
                  key={i}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                  sx={{ width: "100%" }}
                >
                  <SoftBox display="flex" justifyContent="center" alignItems="center">
                    <SoftTypography variant="h6" fontWeight="medium"  mr={2}>
                      {d.dayName}
                    </SoftTypography>
                    <SoftTypography variant="h6" >
                      {d.formattedDate}
                    </SoftTypography>
                  </SoftBox>
                  <SoftSelect
                    sx={{ width: 200 }}
                    value={leaveTypeOptions.find((o) => o.value === d.leaveType)}
                    options={leaveTypeOptions}
                    onChange={(opt) => handleDateLeaveTypeChange(i, opt.value)}
                    isSearchable={false}
                    isDisabled={!selectedPolicy}
                  />
                </SoftBox>
              ))}
            </SoftBox>

            {/* Total Days */}
            <Divider sx={{ my: 1 }} />
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6" fontWeight="medium" >
                Total Days
              </SoftTypography>
              <SoftTypography
                variant="h5"
                fontWeight="medium"
                color={
                  calculateTotalDays() > (selectedPolicy?.stats.available || 0) ? "error" : "info"
                }
              >
                {calculateTotalDays()} day(s)
                {selectedPolicy && calculateTotalDays() > selectedPolicy.stats.available && (
                  <SoftTypography variant="caption" color="error" display="block">
                    Exceeds available balance!
                  </SoftTypography>
                )}
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          {/* Email */}
          {/* <SoftBox mb={3}>
            <SoftTypography variant="h6" fontWeight="medium" >
              Team Email ID
            </SoftTypography>
            <SoftInput
              type="email"
              placeholder="Enter team email ID"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
          </SoftBox> */}

          {/* Reason */}
          <SoftBox mb={1}>
            <SoftTypography variant="h6" fontWeight="medium" >
              Reason for Leave
            </SoftTypography>
            <SoftInput
              multiline
              rows={2}
              placeholder="Optional reason for leave..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
            />
          </SoftBox>

          {/* Buttons */}
          <SoftBox display="flex" gap={2}>
            <SoftButton
              variant="gradient"
              color="info"
              fullWidth
              disabled={loading || !selectedPolicy || calculateTotalDays() <= 0}
              onClick={handleSubmit}
              sx={{ py: 1 }}
            >
              {loading ? (
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} color="inherit" />
                  <SoftTypography variant="button" color="inherit">
                    Submitting...
                  </SoftTypography>
                </SoftBox>
              ) : (
                "Submit Leave Request"
              )}
            </SoftButton>
            <SoftButton
              variant="outlined"
              color="dark"
              fullWidth
              onClick={handleClose}
              disabled={loading}
              sx={{ py: 1.2 }}
            >
              Cancel
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Card>

      {/* --- RIGHT BALANCE PANEL --- */}
      <SoftBox sx={{ width: 320, minWidth: 280 }}>
        <Card
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 3,
            background: "linear-gradient(195deg, #49a3f1, #1A73E8)",
          }}
        >
          <SoftTypography variant="h6" fontWeight="bold" mb={2} color="white">
            {selectedPolicy ? `${selectedPolicy.leave_name} Balance` : "Leave Balance"}
          </SoftTypography>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.3)", }} />
          <Grid container spacing={1.5}>
            <Grid item xs={8}>
              <SoftTypography variant="body2" color="white" opacity={0.9}>
                Available balance
              </SoftTypography>
            </Grid>
            <Grid item xs={4}>
              <SoftTypography variant="body2" align="right" fontWeight="bold" color="white">
                {balanceData.availableBalance}
              </SoftTypography>
            </Grid>
            <Grid item xs={8}>
              <SoftTypography variant="body2" color="white" opacity={0.9}>
                Current booking
              </SoftTypography>
            </Grid>
            <Grid item xs={4}>
              <SoftTypography variant="body2" align="right" fontWeight="bold" color="white">
                {balanceData.currentBooking}
              </SoftTypography>
            </Grid>
            <Grid item xs={8}>
              <SoftTypography variant="body2" color="white" opacity={0.9}>
                Balance after booking
              </SoftTypography>
            </Grid>
            <Grid item xs={4}>
              <SoftTypography
                variant="body2"
                align="right"
                fontWeight="bold"
                color={balanceData.balanceAfterBooking < 0 ? "error" : "white"}
              >
                {balanceData.balanceAfterBooking}
                {balanceData.balanceAfterBooking < 0 && (
                  <SoftTypography variant="caption" color="error" display="block">
                    Negative Balance!
                  </SoftTypography>
                )}
              </SoftTypography>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
          <SoftTypography variant="h6" fontWeight="bold" mb={2}>
            Remaining Leaves
          </SoftTypography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={1.5}>
            <Grid item xs={8}>
              <SoftTypography variant="body2" >
                After this request
              </SoftTypography>
            </Grid>
            <Grid item xs={4}>
              <SoftTypography
                variant="body2"
                align="right"
                fontWeight="bold"
                color={balanceData.estimatedBalance <= 0 ? "error" : "primary"}
              >
                {balanceData.estimatedBalance}
              </SoftTypography>
            </Grid>
          </Grid>

          {/* Leave Type Details */}
          {selectedPolicy && (
            <>
              <Divider sx={{ my: 2 }} />
              <SoftTypography variant="body2" fontWeight="medium"  mb={1}>
                {selectedPolicy.leave_name} ({selectedPolicy.leave_code})
              </SoftTypography>
              <SoftTypography variant="caption" color="text.secondary">
                You can select maximum {selectedPolicy.stats.available} days
              </SoftTypography>
            </>
          )}
        </Card>
      </SoftBox>
    </SoftBox>
  );
}

ApplyLeaveForm.propTypes = {
  selectedLeaveType: PropTypes.string,
  handleClose: PropTypes.func,
  onDateChange: PropTypes.func,
  userId: PropTypes.number,
};

export default ApplyLeaveForm;
